import React from "react";

import {
  TLBaseShape,
  RecordProps,
  T,
  Rectangle2d,
  HTMLContainer,
  BaseBoxShapeUtil,
  Editor,
  TLResizeInfo,
  useValue,
} from "@tldraw/tldraw";
import { getDashArray } from "../new-geo-shape-helpers";
import { mixColors } from "./CustomCloudUtil";
import { HOVER_EFFECT } from "./constants";
import { ShapeUtilHelper } from "./ShapeUtilHelper";
import { ShapeShadowDefs, buildFilterRegion } from "./shadowUtils";
import { defaultScale } from "../constants";

type CustomXBoxUtilType = TLBaseShape<
  "customXBox",
  {
    w: number;
    h: number;
    outlineColor: string;
    fill: string;
    xColor: string;
    dash: "solid" | "dashed" | "dotted" | "dashed solid" | "custom";
    transparency: number;
    strokeDasharray?: string;
    strokeWidth?: number;
    filterBlur?: number;
    borderWidth?: number;
    borderColor?: string;
    borderSolid?: "solid" | "dashed";
    borderTopStyle?: string;
    borderBottomStyle?: string;
    borderLeftStyle?: string;
    borderRightStyle?: string;
    isShowBorderTop?: boolean;
    isShowBorderBottom?: boolean;
    isShowBorderLeft?: boolean;
    isShowBorderRight?: boolean;
    rounded?: number;
    isCreatedFilterBlur?: boolean;
    filterBlurValueWhenHidden?: number;
    outShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    inShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    previousBorderColor?: string;
    isCreatedBorderItem?: boolean;
    colorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    outlineColorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    isHiddenLayerBlur?: boolean;
    isHiddenBorder?: boolean;
    isShowIndicator?: boolean;
    isScale?: boolean;
    scale?: number;
  }
>;

export class CustomXBoxUtil extends BaseBoxShapeUtil<CustomXBoxUtilType> {
  private shapeUtilHelper: ShapeUtilHelper;

  constructor(editor: Editor) {
    super(editor);
    this.shapeUtilHelper = new ShapeUtilHelper(editor);
  }

  static override type = "customXBox" as const;
  static override props: RecordProps<CustomXBoxUtilType> = {
    w: T.number,
    h: T.number,
    outlineColor: T.string,
    fill: T.string,
    xColor: T.string,
    dash: T.any,
    transparency: T.number,
    strokeDasharray: T.optional(T.string),
    strokeWidth: T.optional(T.number),
    filterBlur: T.optional(T.number),
    borderWidth: T.optional(T.number),
    borderColor: T.optional(T.string),
    borderSolid: T.any,
    borderTopStyle: T.optional(T.string),
    borderBottomStyle: T.optional(T.string),
    borderLeftStyle: T.optional(T.string),
    borderRightStyle: T.optional(T.string),
    isShowBorderTop: T.optional(T.boolean),
    isShowBorderBottom: T.optional(T.boolean),
    isShowBorderLeft: T.optional(T.boolean),
    isShowBorderRight: T.optional(T.boolean),
    rounded: T.optional(T.number),
    isCreatedFilterBlur: T.optional(T.boolean),
    filterBlurValueWhenHidden: T.optional(T.number),
    outShadowArray: T.dict(
      T.string,
      T.object({
        id: T.string,
        dx: T.number,
        dy: T.number,
        shadowBlur: T.number,
        color: T.string,
        opacity: T.number,
        isHidden: T.boolean,
      })
    ),
    inShadowArray: T.dict(
      T.string,
      T.object({
        id: T.string,
        dx: T.number,
        dy: T.number,
        shadowBlur: T.number,
        color: T.string,
        opacity: T.number,
        isHidden: T.boolean,
      })
    ),
    previousBorderColor: T.optional(T.string),
    isCreatedBorderItem: T.optional(T.boolean),
    colorMix: T.dict(
      T.string,
      T.object({
        color: T.string,
        isHidden: T.boolean,
      })
    ),
    outlineColorMix: T.dict(
      T.string,
      T.object({
        color: T.string,
        isHidden: T.boolean,
      })
    ),
    isHiddenLayerBlur: T.optional(T.boolean),
    isHiddenBorder: T.optional(T.boolean),
    isShowIndicator: T.optional(T.boolean),
    isScale: T.optional(T.boolean),
    scale: T.optional(T.number),
  };

  getDefaultProps(): CustomXBoxUtilType["props"] {
    return {
      w: 100,
      h: 100,
      outlineColor: "#000000",
      fill: "#e3e3e3",
      xColor: "#000000",
      dash: "solid",
      transparency: 1,
      strokeWidth: 0,
      filterBlur: 0,
      isCreatedFilterBlur: false,
      filterBlurValueWhenHidden: 0,
      borderWidth: 0,
      borderColor: "#000000",
      borderSolid: "solid",
      borderTopStyle: "solid",
      borderBottomStyle: "solid",
      borderLeftStyle: "solid",
      borderRightStyle: "solid",
      isShowBorderTop: true,
      isShowBorderBottom: true,
      isShowBorderLeft: true,
      isShowBorderRight: true,
      rounded: 0,
      outShadowArray: {},
      inShadowArray: {},
      previousBorderColor: "#000000",
      isCreatedBorderItem: false,
      colorMix: {
        "0": { color: "#e3e3e3", isHidden: false },
      },
      outlineColorMix: {},
      isHiddenLayerBlur: false,
      isHiddenBorder: false,
      isShowIndicator: true,
      isScale: false,
      scale: defaultScale,
    };
  }

  getGeometry(shape: CustomXBoxUtilType) {
    return this.shapeUtilHelper.calculateAdjustedDimensions(shape);
  }

  hideSelectionBoundsFg(_shape: CustomXBoxUtilType): boolean {
    return false; // outline visibility is controlled by isChangingStyle in useHideSelectionOnChange
  }
  hideSelectionBoundsBg(shape: CustomXBoxUtilType): boolean {
    return false; // Always show selection bounds regardless of opacity
  }
  hideResizeHandles(shape: CustomXBoxUtilType): boolean {
    return false; // Always show resize handles regardless of opacity
  }
  isAspectRatioLocked(shape: CustomXBoxUtilType): boolean {
    return !!shape?.props?.isScale;
  }
  onResize(shape: CustomXBoxUtilType, info: TLResizeInfo<CustomXBoxUtilType>) {
    if (shape?.props?.isScale) {
      return this.shapeUtilHelper.resizeScaled(shape, info);
    } else {
      return this.shapeUtilHelper.resizeDefault(shape, info);
    }
  }

  component(shape: CustomXBoxUtilType) {
    const {
      w,
      h,
      outlineColor,
      strokeDasharray,
      dash,
      transparency,
      strokeWidth = 0,
      filterBlur,
      borderWidth = 0,
      borderSolid,
      isShowBorderTop,
      isShowBorderBottom,
      isShowBorderLeft,
      isShowBorderRight,
      rounded,
      outShadowArray,
      inShadowArray,
      colorMix,
      outlineColorMix,
      isHiddenLayerBlur,
      isHiddenBorder,
    } = shape.props;

    // Memoized derivations — see CustomRectUtil for the rationale. Cached on
    // the source prop references so a drag (only x/y changes) doesn't re-run
    // `Object.values().filter().map().mixColors()` per shape per pointer move.
    const calculatedFill = React.useMemo(() => {
      const visible = Object.values(colorMix).filter((c) => !c.isHidden);
      return visible.length > 0 ? mixColors(visible.map((c) => c.color)) : "transparent";
    }, [colorMix]);

    const calculatedOutlineColor = React.useMemo(() => {
      const visible = Object.values(outlineColorMix).filter((c) => !c.isHidden);
      return visible.length > 0 ? mixColors(visible.map((c) => c.color)) : "transparent";
    }, [outlineColorMix]);

    // Build the actual filter URL chain from visible shadows (filters use shadow.id, not a per-shape id).
    const shadowFilterId = React.useMemo(() => {
      const visibleOutShadowIds = outShadowArray
        ? Object.values(outShadowArray)
            .filter((s: any) => !s.isHidden)
            .map((s: any) => s.id)
        : [];
      const visibleInShadowIds = inShadowArray
        ? Object.values(inShadowArray)
            .filter((s: any) => !s.isHidden)
            .map((s: any) => s.id)
        : [];
      return [...visibleOutShadowIds, ...visibleInShadowIds].map((id) => `url(#${id})`).join(" ");
    }, [outShadowArray, inShadowArray]);
    const blurFilterUrl = !isHiddenLayerBlur && filterBlur && filterBlur > 0 ? `url(#blur-${shape.id})` : "";
    const fullFilter = [shadowFilterId, blurFilterUrl].filter(Boolean).join(" ") || undefined;
    const shadowOnlyFilter = shadowFilterId || undefined;
    const innerShadowFilterId = `inner-shadow-${shape.id}`;
    const dashArray = strokeDasharray || getDashArray(dash);

    // Subscribe only to the *boolean* "is this shape hovered" — see
    // CustomRectUtil. Avoids O(N) re-renders on every hover transition.
    const editor = this.editor;
    const isHovered = useValue("isHovered", () => editor.getHoveredShapeId() === shape.id, [editor, shape.id]);

    const outlinePath = `M0,0 L${w},${h} M${w},0 L0,${h}`; // Example path for diagonal lines

    return (
      <HTMLContainer id={shape.id} className="relative flex items-center justify-center">
        <svg
          // See CustomRectUtil for the rationale — SVG root clips
          // content to its viewport by default; shadow/blur halos
          // extend past w×h and would be clipped at the SVG boundary
          // without this. Set both the CSS prop and the SVG attribute
          // because not all browsers honor CSS `overflow` on <svg>.
          overflow="visible"
          style={{
            overflow: "visible",
            borderRadius: `${rounded}px`,
            borderTop:
              isShowBorderTop && !isHiddenBorder
                ? `${borderWidth}px ${borderSolid ?? "solid"} ${calculatedOutlineColor}`
                : "none",
            borderBottom:
              isShowBorderBottom && !isHiddenBorder
                ? `${borderWidth}px ${borderSolid ?? "solid"} ${calculatedOutlineColor}`
                : "none",
            borderLeft:
              isShowBorderLeft && !isHiddenBorder
                ? `${borderWidth}px ${borderSolid ?? "solid"} ${calculatedOutlineColor}`
                : "none",
            borderRight:
              isShowBorderRight && !isHiddenBorder
                ? `${borderWidth}px ${borderSolid ?? "solid"} ${calculatedOutlineColor}`
                : "none",
          }}
          viewBox={`-${strokeWidth / 2} -${strokeWidth / 2} ${w + strokeWidth} ${h + strokeWidth}`}
        >
          <defs>
            {/* Gradient definition */}
            {Object.values(colorMix).filter((c) => !c.isHidden).length > 0 && (
              <linearGradient id={`gradient-${shape.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                {Object.values(colorMix)
                  .filter((c) => !c.isHidden)
                  .map((colorObj, index) => (
                    <stop
                      key={index}
                      offset={`${(index / (Object.values(colorMix).filter((c) => !c.isHidden).length - 1)) * 100}%`}
                      stopColor={colorObj.color}
                    />
                  ))}
              </linearGradient>
            )}
            <ShapeShadowDefs
              outShadowArray={outShadowArray}
              inShadowArray={inShadowArray}
              shapeWidth={w}
              shapeHeight={h}
            />

            {/* Blur filter definition. Region scales with the actual
                blur so heavy blurs aren't clipped at the legacy 200%. */}
            {!isHiddenLayerBlur && filterBlur && filterBlur > 0 && (
              <filter id={`blur-${shape.id}`} {...buildFilterRegion({ blur: filterBlur }, w, h)}>
                <feGaussianBlur stdDeviation={filterBlur} />
              </filter>
            )}
          </defs>
          {/* Invisible hitarea to capture pointer events regardless of opacity */}
          <rect
            x={0}
            y={0}
            width={w}
            height={h}
            fill="transparent"
            stroke="none"
            rx={rounded}
            ry={rounded}
            style={{ pointerEvents: "all" }}
          />
          <g style={{ pointerEvents: "none" }}>
            <rect
              x="0"
              y="0"
              width={w}
              height={h}
              fill={calculatedFill}
              stroke={Object.entries(shape.props.outlineColorMix).length > 0 ? calculatedOutlineColor : outlineColor}
              strokeWidth={strokeWidth}
              fillOpacity={transparency}
              strokeOpacity={transparency}
              filter={fullFilter}
            />
            <path
              d={outlinePath}
              fill="none"
              stroke={Object.entries(shape.props.outlineColorMix).length > 0 ? calculatedOutlineColor : outlineColor}
              strokeWidth={(strokeWidth ?? 0) + 1}
              strokeDasharray={dashArray}
              strokeOpacity={transparency}
              filter={shadowOnlyFilter}
            />
          </g>
          {isHovered && (
            <div
              style={{
                position: "absolute",
                top: -borderWidth - strokeWidth / 2,
                left: -borderWidth - strokeWidth / 2,
                width: w + (borderWidth + strokeWidth / 2) * 2,
                height: h + (borderWidth + strokeWidth / 2) * 2,
                pointerEvents: "none",
                zIndex: 999,
                opacity: 1,
                isolation: "isolate",
              }}
            >
              <svg
                width={w + (borderWidth + strokeWidth / 2) * 2}
                height={h + (borderWidth + strokeWidth / 2) * 2}
                viewBox={`0 0 ${w + (borderWidth + strokeWidth / 2) * 2} ${h + (borderWidth + strokeWidth / 2) * 2}`}
                style={{
                  fill: "none",
                  stroke: HOVER_EFFECT.COLOR,
                  strokeWidth: "2",
                  pointerEvents: "none",
                  opacity: 1,
                }}
              >
                <rect
                  x={borderWidth + strokeWidth / 2}
                  y={borderWidth + strokeWidth / 2}
                  width={w}
                  height={h}
                  rx={rounded}
                  ry={rounded}
                  fill="none"
                  stroke={HOVER_EFFECT.COLOR}
                  strokeWidth="2"
                />
              </svg>
            </div>
          )}
        </svg>
      </HTMLContainer>
    );
  }

  addDefaultColorMix(shape: CustomXBoxUtilType) {
    this.shapeUtilHelper.addDefaultColorMix(shape);
  }
  addDefaultOutShadow(shape: CustomXBoxUtilType) {
    this.shapeUtilHelper.addDefaultOutShadow(shape);
  }
  addDefaultInShadow(shape: CustomXBoxUtilType) {
    this.shapeUtilHelper.addDefaultInShadow(shape);
  }

  indicator(shape: CustomXBoxUtilType) {
    const {
      h,
      w,
      rounded,
      borderWidth,
      isShowBorderBottom,
      isShowBorderTop,
      isShowBorderLeft,
      isShowBorderRight,
      isShowIndicator,
      isHiddenBorder,
      strokeWidth = 0,
    } = shape.props;
    const adjustedHeight =
      h +
      strokeWidth +
      (isShowBorderTop && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderBottom && !isHiddenBorder ? borderWidth ?? 0 : 0);

    const adjustedWidth =
      w +
      strokeWidth +
      (isShowBorderLeft && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderRight && !isHiddenBorder ? borderWidth ?? 0 : 0);

    // ALWAYS render indicator, ignore isShowIndicator prop
    return (
      <rect
        x={0}
        y={0}
        rx={rounded}
        ry={rounded}
        width={adjustedWidth}
        height={adjustedHeight}
        stroke="#338bff"
        strokeWidth="2"
        fill="none"
        opacity="1"
        style={{
          stroke: "#338bff",
          strokeWidth: 2,
          fill: "none",
          opacity: 1,
        }}
      />
    );
  }
}
